/* eslint-disable simple-import-sort/imports */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  Resolver,
  useForm,
  type UseFormSetError,
} from 'react-hook-form';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {useFocusEffect} from '@react-navigation/native';
import {zodResolver} from '@hookform/resolvers/zod';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';
import rfdc from 'rfdc';

import AcceptOnlineOrderingTCs from '../../../components/rewards/AcceptOnlineOrderingTCs';
import AskToVerify from '../../../components/rewards/AskToVerify';
import OptIn from '../../../components/rewards/OptIn';
import ShopLoyaltyEnabled from '../../../components/rewards/ShopLoyaltyEnabled';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import TextInput from '../../../components/themed/TextInput';

// Ref type for TextInput - using any due to themed wrapper complexity
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TextInputRef = any;
import ValidationMessage from '../../../components/themed/ValidationMessage';
import AddressEntry from '../../../components/ui-controls/address/AddressEntry';
import DateOfBirthInput from '../../../components/ui-controls/DateOfBirthInput';
import {InputLabel} from '../../../components/ui-controls/form/InputLabel';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Modal from '../../../components/ui-controls/Modal';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../../models/AppRoute';
import {User} from '../../../models/mfl';
import {AccountScreenAction, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import apiService from '../../../transport/apiService';
import {logger, serializeError} from '../../../utils/logger';
import {cleanPhoneNumber} from '../../../utils/phoneNumber';
import TogglePasswordVisibleIconButton from '../components/TogglePasswordVisibleIconButton';
import {PasswordRequirementsIndicator} from '../components/PasswordRequirementsIndicator';
import {WeakPasswordBypass} from '../components/WeakPasswordBypass';
import {usePasswordInput} from '../hooks';
import {
  AccountCreateBaseFormData,
  accountCreateBaseSchema,
  AccountCreatePasswordFormData,
  accountCreatePasswordSchema,
  AccountCreateWithPasswordFormData,
} from '../schemas/authSchemas';

import {styles} from './styles';

const clone = rfdc();

// ============================================================================
// PURE HELPER FUNCTIONS
// ============================================================================

/**
 * Prepare user values for API submission by cleaning up form data
 */
function prepareUserValues(
  values: AccountCreateWithPasswordFormData | AccountCreateBaseFormData,
): Partial<User> {
  const userValues: Partial<User> = clone(values);

  // Remove form-specific fields that shouldn't be sent to API
  delete (userValues as Partial<User> & {optInRewards?: boolean}).optInRewards;

  return userValues;
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for managing form submission state and callbacks
 */
function useFormActions(isCreate: boolean, passwordRef: React.RefObject<TextInputRef>) {
  const {userStore} = useStores();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] = useState(false);

  const postSubmitSuccess = useCallback(() => {
    setIsPasswordUpdatedModalVisible(false);
    userStore.afterUpdateUser();
  }, [userStore]);

  const submitSuccessCallback = useCallback(() => {
    setSubmitting(false);
    if (passwordRef.current) {
      passwordRef.current.clear();
    }
    if (isCreate) {
      setIsPasswordUpdatedModalVisible(true);
      return;
    }
    postSubmitSuccess();
  }, [isCreate, passwordRef, postSubmitSuccess]);

  const submitErrorCallback = useCallback(() => {
    setSubmitting(false);
  }, []);

  return {
    isSubmitting,
    setSubmitting,
    isPasswordUpdatedModalVisible,
    submitSuccessCallback,
    submitErrorCallback,
    postSubmitSuccess,
  };
}

/**
 * Hook for phone number validation
 */
function usePhoneVerification(
  isCreate: boolean,
  formSetError: UseFormSetError<AccountCreatePasswordFormData | AccountCreateBaseFormData>,
) {
  const checkPhoneExists = useCallback(
    async (phoneNumber: string): Promise<boolean> => {
      if (!isCreate) return false;

      const cleanedPhone = cleanPhoneNumber(phoneNumber);
      if (cleanedPhone.length < 12) return false;

      try {
        const result = await apiService.rewardsCheckMobileExists({phoneNumber: cleanedPhone});

        // Handle 500 errors with empty response {} - treat as not exists (skip check)
        if (!result || !result.data) {
          return false;
        }

        if (result?.status === 200 && result.data?.exists) {
          formSetError('phoneNumber', {
            type: 'manual',
            message:
              'This mobile number already exists against an account, please enter a different number. If you believe this is an error please contact support.',
          });
          return true; // Phone exists, block submission
        }
      } catch (_e) {
        // Continue if API call fails (treat as phone available)
      }
      return false;
    },
    [isCreate, formSetError],
  );

  return {
    checkPhoneExists,
  };
}

/**
 * Hook for managing the account form setup
 * @param action - Account screen action (CREATE, UPDATE, COLLECT_MISSING_DETAILS)
 * @param email - Email from route params (for CREATE flow)
 * @returns Form methods, watched password value, action booleans, and signup offers
 */
function useAccountForm(action: AccountScreenAction, email?: string) {
  const {userStore, offersStore} = useStores();

  // Trigger loading of sign-up offers when hook is called
  useEffect(() => {
    const _ = offersStore.signUpOfferData; // Accessing triggers lazy load
  }, [offersStore.signUpOfferData]);

  const signUpOffers = offersStore.signUpOfferData || [];

  const isCreate = action === AccountScreenAction.CREATE;
  const isUpdate = action === AccountScreenAction.UPDATE;
  const isCollectMissingDetails = action === AccountScreenAction.COLLECT_MISSING_DETAILS;

  // Select schema based on action
  const schema = isCreate ? accountCreatePasswordSchema : accountCreateBaseSchema;

  const form = useForm<AccountCreatePasswordFormData | AccountCreateBaseFormData>({
    resolver: zodResolver(schema) as Resolver<
      AccountCreatePasswordFormData | AccountCreateBaseFormData
    >,
    defaultValues: {
      firstName: userStore.firstName,
      lastName: userStore.lastName,
      email: userStore.emailAddress || email, // Prioritize userStore to avoid stale route params
      dateOfBirth: userStore.dateOfBirth,
      phoneNumber: userStore.phoneNumber,
      billingAddress: userStore.billingAddress || {
        lines: [],
        locality: '',
        administrative_area: '',
        postal_code: '',
      },
      ...(isCreate && {
        password: '',
        confirmPassword: '',
      }),
      optInRewards: false, // The Opt-in checkbox must be unselected to begin, we cannot preselect this option.
    },
    mode: 'onTouched', // Validate after user interacts with field
  });

  const passwordValue = form.watch('password');

  // Sync email field when userStore.emailAddress changes (for UPDATE action)
  useEffect(() => {
    if (isUpdate && userStore.emailAddress) {
      const currentEmail = form.getValues('email');
      if (currentEmail !== userStore.emailAddress) {
        form.setValue('email', userStore.emailAddress);
      }
    }
  }, [userStore.emailAddress, isUpdate, form]);

  // Reset form when email route param changes (for CREATE action)
  useFocusEffect(
    useCallback(() => {
      if (isCreate && email) {
        const currentEmail = form.getValues('email');
        // If route param email differs from current form email, reset the form
        if (currentEmail !== email) {
          form.reset({
            firstName: '',
            lastName: '',
            email: email,
            dateOfBirth: null,
            phoneNumber: '',
            billingAddress: {
              lines: [],
              locality: '',
              administrative_area: '',
              postal_code: '',
            },
            password: '',
            confirmPassword: '',
            passwordValidationBypass: false,
            optInRewards: false,
          });
        }
      }
    }, [isCreate, email, form]),
  );

  return {
    form,
    passwordValue,
    isCreate,
    isUpdate,
    isCreateOrUpdate: isCreate || isUpdate,
    isCollectMissingDetails,
    signUpOffers,
    isLoadingSignUpOffers: offersStore.isLoadingSignUpOffers,
  };
}

/**
 * Type for the password input hook return value
 */
type PasswordInputReturn = ReturnType<
  typeof usePasswordInput<AccountCreatePasswordFormData | AccountCreateBaseFormData>
>;

/**
 * Component for rendering password fields (password and confirm password)
 */
interface PasswordFieldsProps {
  control: Control<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  passwordRef: React.RefObject<TextInputRef>;
  confirmPasswordRef: React.RefObject<TextInputRef>;
  isSubmitted: boolean;
  passwordInput: PasswordInputReturn;
}

function PasswordFields(props: PasswordFieldsProps) {
  const {control, passwordRef, confirmPasswordRef, isSubmitted, passwordInput} = props;

  return (
    <>
      <Controller
        control={control}
        name="password"
        render={({field: {onChange, onBlur, value}, fieldState: {isTouched}}) => (
          <>
            <TextInput
              autoCapitalize="none"
              label={<InputLabel required>Password</InputLabel>}
              onBlur={() => {
                passwordInput.passwordFieldHandlers.onBlur();
                onBlur();
              }}
              onFocus={passwordInput.passwordFieldHandlers.onFocus}
              onChangeText={(text: string) => {
                onChange(text);
                passwordInput.passwordFieldHandlers.onChangeText(text);
              }}
              onSubmitEditing={passwordInput.passwordFieldHandlers.onSubmitEditing}
              ref={passwordRef}
              secureTextEntry={!passwordInput.isPasswordVisible}
              value={value || ''}
              touched={isTouched || isSubmitted}
              errorMessage={undefined}
              errorMessageWhenTouched={undefined}
              renderErrorMessage={false}
              rightIconContainerStyle={{
                position: 'absolute',
                right: 10,
              }}
              rightIcon={<TogglePasswordVisibleIconButton {...passwordInput.toggleButtonProps} />}
            />
            <PasswordRequirementsIndicator {...passwordInput.indicatorProps} />
          </>
        )}
      />
      <WeakPasswordBypass {...passwordInput.bypassCheckboxProps} />
      <Controller
        control={control}
        name="confirmPassword"
        render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => {
          const passwordsMatch = value && passwordInput.passwordValue === value;

          // Show error immediately if passwords don't match while typing
          const showError = value && !passwordsMatch;

          return (
            <TextInput
              autoCapitalize="none"
              label={<InputLabel required>Confirm password</InputLabel>}
              onBlur={onBlur}
              onChangeText={(text: string) => {
                onChange(text);
                passwordInput.confirmFieldHandlers.onChangeText(text);
              }}
              ref={confirmPasswordRef}
              secureTextEntry={!passwordInput.isConfirmPasswordVisible}
              value={value || ''}
              touched={isTouched || isSubmitted}
              errorMessage={showError ? 'Passwords do not match' : undefined}
              errorMessageWhenTouched={!showError ? error?.message : undefined}
              rightIconContainerStyle={{
                position: 'absolute',
                right: 10,
              }}
              rightIcon={
                <TogglePasswordVisibleIconButton {...passwordInput.confirmToggleButtonProps} />
              }
            />
          );
        }}
      />
    </>
  );
}

/**
 * Component for rendering phone number field with verification message
 */
interface PhoneNumberFieldProps {
  control: Control<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  errors: FieldErrors<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  action: AccountScreenAction;
  isCreate: boolean;
  isSubmitted: boolean;
  disableField: {mobileNumber: boolean};
}

function PhoneNumberField(props: PhoneNumberFieldProps) {
  const {control, errors: _errors, action, isCreate, isSubmitted, disableField} = props;

  return (
    <>
      <Controller
        control={control}
        name="phoneNumber"
        render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
          <TextInput
            accessibilityLabel="Mobile number"
            autoCapitalize="none"
            autoComplete="tel"
            errorMessageWhenTouched={error?.message}
            keyboardType="phone-pad"
            label={<InputLabel required>Mobile number</InputLabel>}
            maxLength={20}
            onBlur={onBlur}
            onChangeText={onChange}
            value={value || ''}
            disabled={disableField.mobileNumber}
            touched={isTouched || isSubmitted}
            placeholder="e.g. 0412 345 678 or +61 412 345 678"
          />
        )}
      />

      {!!(isCreate || !disableField.mobileNumber) && <AskToVerify action={action} />}
    </>
  );
}

/**
 * Component for rendering billing address and date of birth fields
 */
interface AddressAndDobFieldsProps {
  control: Control<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  errors: FieldErrors<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  setError: UseFormSetError<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  isUpdate: boolean;
  isSubmitted: boolean;
  disableField: {
    billingAddress: boolean;
    dateOfBirth: boolean;
  };
}

function AddressAndDobFields(props: AddressAndDobFieldsProps) {
  const {control, setError, isUpdate, isSubmitted, disableField} = props;

  return (
    <>
      <View style={{marginTop: 8}}>
        <Text variant="body2" style={{marginBottom: 8}}>
          <InputLabel required>Billing Address</InputLabel>
        </Text>
        <Controller
          control={control}
          name="billingAddress"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <>
              <AddressEntry
                testID="billingAddress"
                onChange={address => {
                  onChange(address);
                  // Trigger validation after user completes address entry
                  setTimeout(() => onBlur(), 0);
                }}
                address={
                  value || {
                    lines: [],
                    locality: '',
                    administrative_area: '',
                    postal_code: '',
                  }
                }
                placeholder=""
                disabled={disableField.billingAddress}
              />
              <View style={{minHeight: 30}}>
                {!!((isTouched || isSubmitted) && error) && (
                  <ValidationMessage>Please enter a valid billing address</ValidationMessage>
                )}
              </View>
            </>
          )}
        />
      </View>
      <View style={styles.dateOfBirthContainer}>
        <InputLabel required>Date of Birth</InputLabel>
        <Controller
          control={control}
          name="dateOfBirth"
          render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
            <>
              <DateOfBirthInput
                initialDate={value}
                disabled={isUpdate || disableField.dateOfBirth}
                onChange={dateString => {
                  onChange(dateString);
                  // Trigger validation after user completes date entry
                  setTimeout(() => onBlur(), 0);
                }}
                onInvalidDate={errorMessage => {
                  setError('dateOfBirth', {type: 'manual', message: errorMessage});
                }}
              />
              <ValidationMessage>
                {((isTouched || isSubmitted) && error?.message) || ' '}
              </ValidationMessage>
            </>
          )}
        />
      </View>
    </>
  );
}

/**
 * Component for rendering personal information fields (first name, last name, email)
 */
interface PersonalInfoFieldsProps {
  control: Control<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  errors: FieldErrors<AccountCreatePasswordFormData | AccountCreateBaseFormData>;
  isSubmitted: boolean;
  disableField: {
    firstName: boolean;
    lastName: boolean;
  };
}

function PersonalInfoFields(props: PersonalInfoFieldsProps) {
  const {control, disableField, isSubmitted} = props;

  return (
    <>
      <Controller
        control={control}
        name="firstName"
        render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
          <TextInput
            accessibilityLabel="First name"
            autoCapitalize="words"
            autoComplete="name-given"
            errorMessageWhenTouched={error?.message}
            keyboardType="default"
            label={<InputLabel required>First name</InputLabel>}
            maxLength={50}
            onBlur={onBlur}
            onChangeText={onChange}
            value={value || ''}
            disabled={disableField.firstName}
            touched={isTouched || isSubmitted}
          />
        )}
      />
      <Controller
        control={control}
        name="lastName"
        render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
          <TextInput
            accessibilityLabel="Last name"
            autoCapitalize="words"
            autoComplete="name-family"
            errorMessageWhenTouched={error?.message}
            keyboardType="default"
            label={<InputLabel required>Last name</InputLabel>}
            maxLength={50}
            onBlur={onBlur}
            onChangeText={onChange}
            value={value || ''}
            disabled={disableField.lastName}
            touched={isTouched || isSubmitted}
          />
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({field: {onChange, onBlur, value}, fieldState: {isTouched, error}}) => (
          <TextInput
            accessibilityLabel="Email address"
            autoCapitalize="none"
            autoComplete="email"
            disabled={true}
            editable={false}
            errorMessageWhenTouched={error?.message}
            keyboardType="email-address"
            label={<InputLabel required>Email address</InputLabel>}
            maxLength={100}
            onBlur={onBlur}
            onChangeText={onChange}
            value={value || ''}
            touched={isTouched || isSubmitted}
          />
        )}
      />
    </>
  );
}

const AccountCreateScreen = observer(
  ({
    route,
    navigation,
  }: RouteNavigatorScreenProps<
    AppRoute.ACCOUNT_CREATE | AppRoute.ACCOUNT_DETAIL | AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS
  >) => {
    const {userStore, rewardsStore} = useStores();
    const isLoggedIn = userStore.isLoggedIn;
    const tabBarPadding = useTabBarBottomPadding();

    const action = route.params?.action;
    const email = route.params?.email;

    // Use account form hook for form setup and state
    const accountForm = useAccountForm(action, email);
    const {form, isCreate, isUpdate, isCreateOrUpdate, isCollectMissingDetails} = accountForm;

    // Fetch fresh user info on mount for UPDATE action to ensure email and other fields are current
    useEffect(() => {
      if (isUpdate && isLoggedIn) {
        userStore.retrieveUserInfoV2({retryable: true}).catch(err => {
          logger.error(
            'AccountCreateScreen',
            'Failed to fetch user info on mount',
            serializeError(err),
          );
        });
      }
    }, [isUpdate, isLoggedIn, userStore]);

    const onSubmit = async (
      values: AccountCreateWithPasswordFormData | AccountCreateBaseFormData,
    ) => {
      formActions.setSubmitting(true);

      // For CREATE flow, check phone number first (no account -> no password)
      const phoneExists = await phoneVerification.checkPhoneExists(values.phoneNumber || '');
      if (phoneExists) {
        formActions.setSubmitting(false); // Reset submitting state
        return; // Stop submission if phone exists
      }

      // After phone is available, check password strength (if not bypassed)
      if (isCreate && !values.passwordValidationBypass) {
        const _userValues = prepareUserValues(values);

        let passwordIsOkResponse: {password_is_ok?: boolean} | null = null;

        try {
          passwordIsOkResponse = await apiService.checkPasswordStrength(_userValues as User);
        } catch (error: unknown) {
          const axiosError = error as {
            response?: {
              data?: {
                error?: {
                  errors?: Array<{code?: string; detail?: string}>;
                };
              };
            };
          };

          const apiErrors = axiosError?.response?.data?.error?.errors;
          const weakErr = apiErrors?.find(err => err.code === 'sign_up_form_password_too_weak');

          formActions.setSubmitting(false);
          passwordInput.handleWeakPasswordError({
            status: 'PASSWORD_TOO_WEAK',
            message: weakErr?.detail || '',
          });
          return;
        }

        if (!passwordIsOkResponse?.password_is_ok) {
          formActions.setSubmitting(false);
          passwordInput.handleWeakPasswordError({
            status: 'PASSWORD_TOO_WEAK',
            message: '',
          });
          return;
        }
      }

      const showSuccessToast = !isCreate;

      if (isLoggedIn) {
        const shouldVerifyMobileNumber = userStore.phoneNumber !== values.phoneNumber;

        const userValues: User = {
          ...values,
          billingAddress: values.billingAddress
            ? {
                ...values.billingAddress,
                lines: values.billingAddress.lines || [],
              }
            : undefined,
        };

        if (isUpdate && shouldVerifyMobileNumber) {
          userStore.updateUserFormValues({
            ...clone(values),
            signUpCode: rewardsStore.signUpCode || undefined,
          });
          await apiService.sendMobileVerificationCode({
            phoneNumber: values.phoneNumber,
          });
          navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION, {
            action,
          });
        } else {
          await userStore.updateUserInfo(
            action,
            userValues,
            formActions.submitSuccessCallback,
            formActions.submitErrorCallback,
            {
              showSuccessToast,
            },
          );
        }
      } else {
        // CREATE flow: password already validated above
        userStore.updateUserFormValues({
          ...clone(values),
          signUpCode: rewardsStore.signUpCode || undefined,
        });
        await apiService.sendMobileVerificationCode({
          phoneNumber: values.phoneNumber,
        });

        formActions.setSubmitting(false);
        navigation.navigate(AppRoute.ACCOUNT_PHONE_VERIFICATION);
      }
      formActions.setSubmitting(false);
    };

    const passwordRef = useRef<TextInputRef>(null);
    const confirmPasswordRef = useRef<TextInputRef>(null);

    // Use form actions hook for submission state and callbacks
    const formActions = useFormActions(isCreate, passwordRef);

    // Password input hook (visibility, focus, weak password handling)
    const passwordInput = usePasswordInput({
      form,
      confirmFieldName: 'confirmPassword',
      passwordRef,
      confirmPasswordRef,
    });

    // Extract reset for cleanup
    const {reset: resetPasswordInput} = passwordInput;

    // Phone verification hook
    const phoneVerification = usePhoneVerification(isCreate, form.setError);

    const disableField = {
      firstName: isCollectMissingDetails && userStore.firstName.trim().length > 0,
      lastName: isCollectMissingDetails && userStore.lastName.trim().length > 0,
      dateOfBirth: !!(
        isCollectMissingDetails &&
        userStore.dateOfBirth &&
        userStore.dateOfBirth.length > 0
      ),
      phoneNumber: isCollectMissingDetails && userStore.phoneNumber.trim().length > 0,
      billingAddress: !!(
        isCollectMissingDetails &&
        userStore.billingAddress &&
        userStore.billingAddress.lines.length !== 0
      ),
      mobileNumber: isCollectMissingDetails && userStore.phoneNumber.trim().length > 0,
    };

    // Reset password input state when navigating away
    useFocusEffect(
      useCallback(() => {
        return () => {
          resetPasswordInput();
        };
      }, [resetPasswordInput]),
    );

    const handleSubmitPress = form.handleSubmit(onSubmit);

    return (
      <ThemeProvider theme={rewardsTheme}>
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          enableOnAndroid
          extraScrollHeight={40}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          <ContentView>
            <View style={{gap: 8}}>
              {!!(isCreateOrUpdate || isCollectMissingDetails) && (
                <>
                  <View>
                    <Text style={{marginVertical: 16}}>
                      {isCollectMissingDetails
                        ? 'We seem to be missing some details from your account. Please verify and fill out the details below to continue.'
                        : 'Fill out your details below to create an account to enjoy online shopping and member rewards'}
                    </Text>
                    <Text
                      style={{
                        fontSize: 13,
                        color: '#6B7280',
                        fontStyle: 'italic',
                        marginBottom: 8,
                      }}>
                      * indicates required field
                    </Text>
                  </View>

                  <PersonalInfoFields
                    control={form.control}
                    errors={form.formState.errors}
                    isSubmitted={form.formState.isSubmitted}
                    disableField={{
                      firstName: disableField.firstName,
                      lastName: disableField.lastName,
                    }}
                  />
                </>
              )}
              {!!isCreate && (
                <PasswordFields
                  control={form.control}
                  passwordRef={passwordRef}
                  confirmPasswordRef={confirmPasswordRef}
                  isSubmitted={form.formState.isSubmitted}
                  passwordInput={passwordInput}
                />
              )}

              {!!(isCreateOrUpdate || isCollectMissingDetails) && (
                <>
                  <AddressAndDobFields
                    control={form.control}
                    errors={form.formState.errors}
                    setError={form.setError}
                    isUpdate={isUpdate}
                    isSubmitted={form.formState.isSubmitted}
                    disableField={{
                      billingAddress: disableField.billingAddress,
                      dateOfBirth: disableField.dateOfBirth,
                    }}
                  />

                  {!!isCreate && (
                    <ShopLoyaltyEnabled>
                      <Controller
                        control={form.control}
                        name="optInRewards"
                        render={({field: {onChange, value}}) => (
                          <OptIn checked={!!value} onChange={() => onChange(!value)} />
                        )}
                      />
                    </ShopLoyaltyEnabled>
                  )}

                  <PhoneNumberField
                    control={form.control}
                    errors={form.formState.errors}
                    action={action}
                    isCreate={isCreate}
                    isSubmitted={form.formState.isSubmitted}
                    disableField={{mobileNumber: disableField.mobileNumber}}
                  />

                  {!!isCreate && <AcceptOnlineOrderingTCs />}
                </>
              )}
              <View style={{alignItems: 'flex-end', marginTop: 16}}>
                <Button
                  title={isLoggedIn ? 'Save' : 'Next'}
                  onPress={handleSubmitPress}
                  disabled={formActions.isSubmitting}
                  loading={formActions.isSubmitting}
                  buttonStyle={{width: 194}}
                />
              </View>
            </View>
          </ContentView>
          <Modal
            isVisible={formActions.isPasswordUpdatedModalVisible}
            title="Password updated successfully">
            <Button
              title="Continue"
              onPress={formActions.postSubmitSuccess}
              buttonStyle={{width: 150, marginTop: 16}}
            />
          </Modal>
        </KeyboardAwareScrollView>
      </ThemeProvider>
    );
  },
);

export default AccountCreateScreen;
